import { clsx } from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CardShell } from "@/components/Common";
import Icon from "@/components/Icon";
import { NadNrCreditGroup } from "@/data/models/CreditGroups";
import { calculatePaymentsRemaining, formatDate } from "@/utils/credit-groups";

type Props = {
  item: NadNrCreditGroup;
};

export default function NADNRPlanCard({ item }: Props) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const plan = item.associated_payment_plan;
  const dosage = item.plan_dosage_information;
  const bookableServices = dosage?.bookable_services ?? [];
  const paymentsRemaining = calculatePaymentsRemaining(plan);
  const showLoadingDose =
    paymentsRemaining > 0 || !!plan?.loading_dose_expiration_datetime;

  const groupServiceName = /niagen/i.test(item.group_service_name)
    ? "Niagen"
    : "NAD+";

  // Infer dosage from card name if plan_dosage_information is null
  // Calculation: credits_remaining × add on credit dosage
  const getCreditsDosage = () => {
    if (dosage) return null; // Backend provides dosage, no need to infer

    const dosageMatch = item.group_service_name.match(/(\d+)mg/i);

    if (dosageMatch) {
      const dosagePerCredit = parseInt(dosageMatch[1], 10);
      const totalDosage = item.credits_remaining * dosagePerCredit;
      return totalDosage;
    }

    return null;
  };

  const creditsDosage = getCreditsDosage();
  const hasDosage =
    (dosage !== null && dosage !== undefined) || creditsDosage !== null;

  const handleBook = () =>
    navigate(`/book?store=${encodeURIComponent(item.store_code)}`);

  const contentBaseClasses =
    "w-full overflow-hidden p-4 font-poppins text-sm leading-[140%] text-neutral-dark transition-all";

  return (
    <CardShell>
      {/* Header */}
      <div className="flex justify-between gap-7 p-4">
        <div className="flex flex-col items-start gap-2">
          <span className="font-poppins text-sm font-semibold leading-[14px] text-deep-blue">
            {item.group_service_name}
          </span>
          <span className="font-poppins text-xs text-[#9ca3af]">
            {item.store_description}
          </span>
        </div>
        <button type="button" onClick={handleBook} className="cursor-pointer">
          <Icon name="calendar-plus" />
        </button>
      </div>

      {/* Plan Summary */}
      {showLoadingDose && (
        <div className="flex flex-col border-t border-[#d1d5db] p-4 font-poppins text-xs leading-[133.33%] text-neutral-darkest">
          <span>Loading Dose Plan:</span>
          <ul className="list-inside list-disc pl-2">
            {paymentsRemaining > 0 && (
              <li>
                Payments Remaining: <strong>{paymentsRemaining}</strong>
              </li>
            )}
            {plan?.loading_dose_expiration_datetime && (
              <li>
                Doses Expire:{" "}
                <strong>
                  {formatDate(
                    plan.loading_dose_expiration_datetime,
                    item.store_tz,
                  )}
                </strong>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Plan Details Toggle */}
      <div className="border-t border-[#d1d5db] bg-[#e6f0f9]">
        {hasDosage ? (
          <>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="flex w-full items-center justify-between gap-2 p-4"
            >
              <strong className="font-poppins text-sm font-semibold leading-[14px] text-deep-blue">
                {creditsDosage ? "Details" : "Plan Details"}
              </strong>
              <Icon
                name="arrow"
                className={clsx(
                  "mr-3.5 size-4 -rotate-90 text-neutral-darkest transition-all",
                  open && "rotate-90",
                )}
              />
            </button>

            {/* Plan Details Content */}
            {open && (
              <div
                className={clsx(
                  contentBaseClasses,
                  "max-h-0",
                  open && "max-h-96",
                )}
              >
                {dosage ? (
                  bookableServices.length > 0 ? (
                    <>
                      <p>
                        You have{" "}
                        <strong>
                          {dosage.remaining_dosage}mg of {groupServiceName}
                        </strong>{" "}
                        available, which can be used in the following ways:
                      </p>
                      <div>
                        {bookableServices.map((service) => (
                          <div key={service}>
                            <strong>
                              {" • "}
                              {service}
                            </strong>
                          </div>
                        ))}
                      </div>
                      <p>
                        Choose the option that best supports your wellness
                        journey.
                      </p>
                    </>
                  ) : dosage.remaining_dosage < 125 ? (
                    <p>
                      You have less than 125mg of{" "}
                      <strong>{groupServiceName}</strong> available. At this
                      time, no services can be booked with your remaining
                      balance.
                    </p>
                  ) : (
                    <p>
                      You have{" "}
                      <strong>
                        {dosage.remaining_dosage}mg of {groupServiceName}
                      </strong>{" "}
                      available.
                    </p>
                  )
                ) : creditsDosage !== null ? (
                  <p>
                    You have{" "}
                    <strong>
                      {creditsDosage}mg of {groupServiceName}
                    </strong>{" "}
                    available.
                  </p>
                ) : null}
              </div>
            )}
          </>
        ) : null}
      </div>
    </CardShell>
  );
}
